import { getField, updateField } from 'vuex-map-fields';
import {
  getSubmitInvoiceForm, getFormValidations,
} from '~/config/formConfig';

const defaultState = () => ({
  dialogOpened: false,
  sendExistingInvoice: false,
  fromOverview: false,
  errorMessage: null,
  copyToSender: false,
  isdoc: false,
  message: null,
  recipientEmail: [],
  invoiceId: null,
  invoiceNumber: null,
  rules: null,
  senderEmail: null,
  subject: null,
});

const mutations = {
  updateField,
  openDialog: (state) => {
    state.dialogOpened = true;
  },
  setSendExistingInvoice: (state, value) => {
    state.sendExistingInvoice = value;
  },
  setFromOverview: (state, value) => {
    state.fromOverview = value;
  },
  setInvoiceNumber: (state, value) => {
    state.invoiceNumber = value;
  },
  resetState(state) {
    Object.assign(state, defaultState());
  },
  setErrorMessage: (state, message) => {
    state.errorMessage = message;
  },
  setFormData: (state, data) => {
    state.senderEmail = data.sender;
    state.subject = data.subject;
    state.message = data.body;
    state.invoiceId = data.invoice_id;
    if (data.recipient) {
      state.recipientEmail = data.recipient.split(/,|;/);
    }
  },
};

const actions = {
  closeDialog: ({ commit }) => {
    commit('resetState');
  },
  buildFormForExistingInvoice: ({ commit, rootGetters }, { id, number, fromOverview = false }) => {
    commit('setSendExistingInvoice', true);
    commit('setFromOverview', fromOverview);
    commit('setInvoiceNumber', number);
    commit(
      'api/fetch',
      {
        url: '/send-invoice/build',
        method: 'POST',
        headers: {
          ...rootGetters['auth/getHeaders'],
        },
        body: {
          invoice_id: id,
        },
        onSuccess: ['submitInvoiceForm/formBuildSuccess'],
        onError: ['submitInvoiceForm/formBuildError'],
      },
      { root: true },
    );
  },
  buildForm: ({ commit, rootGetters, getters }) => {
    commit('setSendExistingInvoice', false);
    commit(
      'api/fetch',
      {
        url: '/send-invoice/build',
        method: 'POST',
        headers: {
          ...rootGetters['auth/getHeaders'],
        },
        body: {
          invoice: {
            ...rootGetters['invoiceForm/invoiceApiData'],
          },
          ...getters.getInvoiceId,
        },
        onSuccess: ['submitInvoiceForm/formBuildSuccess'],
        onError: ['submitInvoiceForm/formBuildError'],
      },
      { root: true },
    );
  },
  formBuildSuccess: ({ commit }, action) => {
    commit('setFormData', action.payload.data);
    commit('openDialog');
  },
  formBuildError: () => {
    this.dispatch(
      'flashMessages/addMessage',
      {
        kind: 'error',
        key: 'invoiceBuildFormError',
      },
    );
  },
  submitForm: ({
    commit, rootGetters, getters, state,
  }, { attachmentIds = [] }) => new Promise((resolve, reject) => {
    const invoiceId = getters.getInvoiceId.invoice_id;

    const onSuccess = [
      {
        type: 'analytics/trackEvent',
        analytics: {
          action: state.fromOverview ? 'old' : 'new',
          category: 'invoice',
          label: 'submit',
          value: invoiceId,
        },
      },
    ];

    if (invoiceId) {
      onSuccess.push({
        type: 'api/refetchInvoicesByIds',
        ids: [invoiceId],
      });
    }

    commit(
      'api/fetch',
      {
        url: '/send-invoice',
        method: 'POST',
        headers: {
          ...rootGetters['auth/getHeaders'],
        },
        invoiceId: state.invoiceId,
        body: {
          invoice_email: {
            sender: state.senderEmail,
            recipients_str: state.recipientEmail.join(';'),
            subject: state.subject,
            isdoc_required: state.isdoc,
            copy_to_sender: state.copyToSender,
            body: state.message,
            attachment_ids: attachmentIds,
          },
          ...getters.getInvoiceId,
          ...getters.getInvoiceData,
        },
        onSuccess: [
          {
            type: 'submitInvoiceForm/formSubmitSuccess',
          },
          ...onSuccess,
          'submitInvoiceForm/openPremiumDialog',
        ],
        onError: ['submitInvoiceForm/formSubmitError'],
        promise: {
          resolve,
          reject,
        },
      },
      { root: true },
    );
  }),
  formSubmitError: ({ commit }, action) => {
    const { error } = action.payload.response.data;
    commit('setErrorMessage', error || 'invoiceSendError');
  },
  formSubmitSuccess({ commit }, action) {
    const { count, ...params } = action.data;

    const key = count > 1
      ? 'invoiceSendSuccessfulMultiple'
      : 'invoiceSendSuccessful';

    this.dispatch(
      'flashMessages/addMessage',
      {
        kind: 'success',
        key,
        params,
        withTimeout: true,
        persistent: action.persistent,
      },
    );

    this.dispatch('uiEffects/scrollTo', {
      top: 0,
      left: 0,
    });

    commit('resetState');
  },
  openPremiumDialog() {
    this.dispatch('invoiceForm/openPremiumDialog', 'manage');
  },
  getRecipientSuggestions({ commit, rootGetters }, query = '') {
    return new Promise((resolve, reject) => {
      commit(
        'api/fetch',
        {
          url: '/services/invoice-email-suggestions',
          method: 'POST',
          body: {
            query,
          },
          headers: rootGetters['auth/getHeaders'],
          promise: {
            resolve,
            reject,
          },
        },
        { root: true },
      );
    });
  },

};

/* eslint no-shadow: ["error", { "allow": ["getters"] }] */

const getters = {
  getField,
  validationRules: state => getFormValidations(getSubmitInvoiceForm(state)),
  emailForm: state => state,
  getInvoiceId: (state, getters, rootState, rootGetters) => {
    const invoiceId = rootGetters['invoiceForm/invoiceId'];
    if (state.invoiceId !== null) {
      return { invoice_id: state.invoiceId };
    } if (invoiceId !== null) {
      return { invoice_id: invoiceId };
    }
    return {};
  },
  getInvoiceNumber: state => state.invoiceNumber,
  getInvoiceData: (state, getters, rootState, rootGetters) => {
    if (state.sendExistingInvoice === false) {
      return {
        invoice: {
          ...rootGetters['invoiceForm/invoiceApiData'],
        },
      };
    }
    return {};
  },
};

export default {
  namespaced: true,
  state: defaultState(),
  getters,
  actions,
  mutations,
};
